// ===========================================
// SERVICIO: Usuarios Multi-Tenant
// ===========================================
//
// Este servicio trabaja con usuarios_brainhub y usuario_organizaciones.
// Usa las Netlify Functions para operaciones que requieren Auth0.
//

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database, Json } from '@/types/database'
import type {
  Rol,
  RolMultiTenant,
  Permisos,
} from '@/types/roles'

// Variable para almacenar el getter del token (se configura desde los hooks)
let getAccessTokenFn: (() => Promise<string>) | null = null

/**
 * Configura la función para obtener el token de Auth0
 * Debe llamarse desde el hook que tiene acceso a useAuth0
 */
export function setAccessTokenGetter(fn: () => Promise<string>) {
  getAccessTokenFn = fn
}

/**
 * Helper para hacer fetch con autenticación
 */
async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  if (!getAccessTokenFn) {
    throw new Error('Token getter no configurado. Llama a setAccessTokenGetter primero.')
  }

  const token = await getAccessTokenFn()

  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
}

// ===========================================
// TIPOS
// ===========================================

export interface UsuarioConMembresia {
  id: string
  email: string
  nombre: string | null
  avatar_url: string | null
  auth0_user_id: string | null
  activo: boolean
  ultimo_acceso: string | null
  created_at: string
  // Datos de membresía
  membresia_id: string | null
  rol: RolMultiTenant | null
  workspaces: string[]
  permisos_extra: Partial<Permisos> | null
  permisos_denegados: (keyof Permisos)[] | null
  updated_at: string | null
  organizacion_id: string | null
  organizacion_nombre: string | null
}

export interface FiltrosUsuarios {
  rol?: RolMultiTenant
  activo?: boolean
  busqueda?: string
}

export interface InvitarUsuarioParams {
  email: string
  name?: string
  role: RolMultiTenant
  workspaces?: string[]
  organizacionId?: string
}

export interface EstadisticasUsuarios {
  total: number
  activos: number
  porRol: Record<RolMultiTenant, number>
}

// ===========================================
// SERVICIO
// ===========================================

/**
 * Servicio para gestión de usuarios multi-tenant.
 *
 * IMPORTANTE: Los métodos que usan Supabase reciben el cliente autenticado como primer parámetro.
 * Los métodos que usan Netlify Functions (Auth0) siguen usando fetchWithAuth.
 *
 * @example
 * const { supabase } = useSupabase()
 * const usuarios = await usuariosService.listar(supabase!, organizacionId, filtros)
 */
export const usuariosService = {
  /**
   * Lista todos los usuarios de la organización actual
   */
  async listar(
    client: SupabaseClient<Database>,
    organizacionId: string,
    filtros?: FiltrosUsuarios
  ): Promise<UsuarioConMembresia[]> {
    let query = client
      .from('usuario_organizaciones')
      .select(`
        id,
        rol,
        workspaces,
        permisos_extra,
        permisos_denegados,
        updated_at,
        activo,
        usuario:usuarios_brainhub(
          id,
          email,
          nombre,
          avatar_url,
          auth0_user_id,
          activo,
          ultimo_acceso,
          created_at
        ),
        organizacion:organizaciones(
          id,
          nombre
        )
      `)
      .eq('organizacion_id', organizacionId)
      .order('created_at', { ascending: false })

    if (filtros?.rol) {
      query = query.eq('rol', filtros.rol)
    }

    if (filtros?.activo !== undefined) {
      query = query.eq('activo', filtros.activo)
    }

    const { data, error } = await query

    if (error) throw new Error(`Error al listar usuarios: ${error.message}`)

    // Transformar a formato plano
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let usuarios: UsuarioConMembresia[] = (data || []).map((m: any) => ({
      id: m.usuario?.id,
      email: m.usuario?.email,
      nombre: m.usuario?.nombre,
      avatar_url: m.usuario?.avatar_url,
      auth0_user_id: m.usuario?.auth0_user_id,
      activo: m.activo,
      ultimo_acceso: m.usuario?.ultimo_acceso,
      created_at: m.usuario?.created_at,
      membresia_id: m.id,
      rol: m.rol,
      workspaces: m.workspaces || [],
      permisos_extra: m.permisos_extra,
      permisos_denegados: m.permisos_denegados,
      updated_at: m.updated_at,
      organizacion_id: m.organizacion?.id,
      organizacion_nombre: m.organizacion?.nombre,
    }))

    // Filtrar por búsqueda (cliente-side)
    if (filtros?.busqueda) {
      const busqueda = filtros.busqueda.toLowerCase()
      usuarios = usuarios.filter(
        (u) =>
          u.email?.toLowerCase().includes(busqueda) ||
          u.nombre?.toLowerCase().includes(busqueda)
      )
    }

    return usuarios
  },

  /**
   * Obtiene un usuario por ID
   */
  async obtenerPorId(client: SupabaseClient<Database>, usuarioId: string): Promise<UsuarioConMembresia | null> {
    const { data, error } = await client
      .from('usuarios_brainhub')
      .select(`
        id, email, nombre, apellido, avatar_url, preferencias, activo, ultimo_acceso, ultimo_login, created_at, updated_at, auth0_user_id,
        membresias:usuario_organizaciones(
          id,
          rol,
          workspaces,
          permisos_extra,
          permisos_denegados,
          updated_at,
          activo,
          organizacion:organizaciones(id, nombre)
        )
      `)
      .eq('id', usuarioId)
      .single()

    if (error) throw new Error(`Error al obtener usuario: ${error.message}`)
    if (!data) return null

    const membresia = data.membresias?.[0]

    return {
      id: data.id,
      email: data.email,
      nombre: data.nombre,
      avatar_url: data.avatar_url,
      auth0_user_id: data.auth0_user_id,
      activo: membresia?.activo ?? data.activo ?? false,
      ultimo_acceso: data.ultimo_acceso,
      created_at: data.created_at ?? '',
      membresia_id: membresia?.id ?? null,
      rol: (membresia?.rol ?? null) as RolMultiTenant | null,
      workspaces: membresia?.workspaces || [],
      permisos_extra: (membresia?.permisos_extra ?? null) as Partial<Permisos> | null,
      permisos_denegados: (membresia?.permisos_denegados ?? null) as (keyof Permisos)[] | null,
      updated_at: membresia?.updated_at ?? null,
      organizacion_id: membresia?.organizacion?.id ?? null,
      organizacion_nombre: membresia?.organizacion?.nombre ?? null,
    }
  },

  /**
   * Invitar un nuevo usuario (usa Netlify Function)
   */
  async invitar(params: InvitarUsuarioParams): Promise<{ success: boolean; user: UsuarioConMembresia }> {
    const response = await fetchWithAuth('/.netlify/functions/auth0-invite', {
      method: 'POST',
      body: JSON.stringify(params),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Error invitando usuario')
    }

    return data
  },

  /**
   * Enviar email de reset de password (usa Netlify Function)
   */
  async resetPassword(auth0UserId: string): Promise<void> {
    const response = await fetchWithAuth('/.netlify/functions/auth0-reset-password', {
      method: 'POST',
      body: JSON.stringify({ auth0_user_id: auth0UserId }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Error enviando reset de password')
    }
  },

  /**
   * Bloquear/desbloquear usuario (usa Netlify Function)
   */
  async toggleBloqueo(auth0UserId: string, block: boolean): Promise<void> {
    const response = await fetchWithAuth('/.netlify/functions/auth0-block', {
      method: 'POST',
      body: JSON.stringify({ auth0_user_id: auth0UserId, block }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Error bloqueando/desbloqueando usuario')
    }
  },

  /**
   * Eliminar usuario (usa Netlify Function)
   */
  async eliminar(auth0UserId: string): Promise<void> {
    const response = await fetchWithAuth('/.netlify/functions/auth0-delete', {
      method: 'DELETE',
      body: JSON.stringify({ auth0_user_id: auth0UserId }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Error eliminando usuario')
    }
  },

  /**
   * Cambiar rol de un usuario (RPC server-side cambiar_rol_seguro: valida
   * gestionarUsuarios, jerarquía owner, last-admin guard, resetea overrides y
   * audita en la misma transacción). updatedAt = lock optimista.
   */
  async cambiarRol(
    client: SupabaseClient<Database>,
    membresiaId: string,
    nuevoRol: RolMultiTenant,
    updatedAt: string
  ): Promise<void> {
    const { error } = await client.rpc('cambiar_rol_seguro', {
      p_membresia_id: membresiaId,
      p_rol: nuevoRol,
      p_updated_at: updatedAt,
    })

    if (error) throw new Error(`Error cambiando rol: ${error.message}`)
  },

  /**
   * Actualizar workspaces de un usuario (RPC server-side actualizar_workspaces_seguro).
   */
  async actualizarWorkspaces(
    client: SupabaseClient<Database>,
    membresiaId: string,
    workspaces: string[],
    updatedAt: string
  ): Promise<void> {
    const { error } = await client.rpc('actualizar_workspaces_seguro', {
      p_membresia_id: membresiaId,
      p_workspaces: workspaces,
      p_updated_at: updatedAt,
    })

    if (error) throw new Error(`Error actualizando workspaces: ${error.message}`)
  },

  /**
   * Actualizar permisos_extra / permisos_denegados de un usuario (override
   * por-usuario). RPC actualizar_permisos_seguro: valida gestionarUsuarios,
   * denylist de flags meta, input, lock optimista y audita.
   */
  async actualizarPermisos(
    client: SupabaseClient<Database>,
    membresiaId: string,
    extra: Partial<Permisos>,
    denegados: (keyof Permisos)[],
    updatedAt: string
  ): Promise<void> {
    const { error } = await client.rpc('actualizar_permisos_seguro', {
      p_membresia_id: membresiaId,
      p_extra: extra as Json,
      p_denegados: denegados as string[],
      p_updated_at: updatedAt,
    })

    if (error) throw new Error(`Error actualizando permisos: ${error.message}`)
  },

  /**
   * Actualizar la política de permisos de un ROL para la organización (capa
   * org-rol, RPC actualizar_permisos_rol_seguro). `cambios` = { flag: true|false
   * para setear el override, null para limpiarlo }. Solo roles no privilegiados.
   */
  async actualizarPermisosRol(
    client: SupabaseClient<Database>,
    organizacionId: string,
    rol: Extract<Rol, 'contador_asociado' | 'asistente' | 'solo_lectura'>,
    cambios: Record<string, boolean | null>
  ): Promise<void> {
    const { error } = await client.rpc('actualizar_permisos_rol_seguro', {
      p_org: organizacionId,
      p_rol: rol,
      p_cambios: cambios as Json,
    })

    if (error) throw new Error(`Error actualizando permisos del rol: ${error.message}`)
  },

  /**
   * Overrides org-rol vigentes de la organización (capa configurable por rol).
   */
  async obtenerOverridesRol(
    client: SupabaseClient<Database>,
    organizacionId: string
  ): Promise<{ rol: string; flag: string; permitido: boolean }[]> {
    const { data, error } = await client
      .from('rbac_org_rol_override')
      .select('rol, flag, permitido')
      .eq('organizacion_id', organizacionId)

    if (error) throw new Error(`Error obteniendo overrides de rol: ${error.message}`)

    return data ?? []
  },

  /**
   * Obtener estadísticas de usuarios
   */
  async obtenerEstadisticas(client: SupabaseClient<Database>, organizacionId: string): Promise<EstadisticasUsuarios> {
    const { data, error } = await client
      .from('usuario_organizaciones')
      .select('rol, activo')
      .eq('organizacion_id', organizacionId)

    if (error) throw new Error(`Error obteniendo estadísticas: ${error.message}`)

    const porRol: Record<RolMultiTenant, number> = {
      owner: 0,
      admin: 0,
      profesional: 0,
      asistente: 0,
      solo_lectura: 0,
    }

    let activos = 0

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(data || []).forEach((m: any) => {
      if (m.rol in porRol) {
        porRol[m.rol as RolMultiTenant]++
      }
      if (m.activo) activos++
    })

    return {
      total: data?.length || 0,
      activos,
      porRol,
    }
  },
}
